import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getMedicalConditions, getPatientActivity, getPatientAllergies, getPatientDetails, getPatientDiagnosis, getPatientFamilyHistory, getPatientMeasurements, getPatientMedicalConditions, getPatientMedication } from '@uplift/api';
import { BaseQueryOpts } from '../baseTypes';
import sortBy from 'lodash/sortBy';

export const usePatientDetailsQuery = (patientId: string, opts?: BaseQueryOpts) =>
  useQuery({
    queryKey: [ 'user', patientId, 'details' ],
    queryFn: async () => await getPatientDetails(patientId),
    select: (response) => response.data,
    ...opts,
  });


export const usePatientActivityQuery = (patientId: string, opts?: BaseQueryOpts) =>
  useInfiniteQuery({
    queryKey: [ 'user', patientId, 'activity' ],
    queryFn: async ({ pageParam }) => await getPatientActivity(patientId, { cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage?.cursor,
    ...opts,
  });

export const usePatientDiagnosisQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'diagnosis' ], async () => {
    const response = await getPatientDiagnosis(patientId);

    return response.data;
  }, opts);
};

export const usePatientFamilyHistory = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'family-history' ], async () => {
    const response = await getPatientFamilyHistory(patientId);

    return response.data;
  }, {
    ...opts,
    retry: 0,
  });
};

export const usePatientMedicationQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'patient-medication' ], async () => {
    const response = await getPatientMedication(patientId);

    return response.data;
  }, {
    ...opts,
    retry: 0,
  });
};

export const usePatientAllergiesQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'patient-allergies' ], async () => {
    const response = await getPatientAllergies(patientId);

    return response.data;
  }, {
    ...opts,
    retry: 0,
  });
};


export const useMedicalConditionsQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'medicalConditions' ],
    queryFn: getMedicalConditions,
    select: res => sortBy(res.data, 'category', 'name'),
    ...opts,
  });
};


export const usePatientMedicalConditionsQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'patient-medical-conditions' ], async () => {
    const response = await getPatientMedicalConditions(patientId);

    return response.data;
  }, {
    ...opts,
    retry: 0,
  });
};

export const usePatientMeasurementsQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery([ 'user', patientId, 'patient-measurements' ], async () => {
    const response = await getPatientMeasurements(patientId);

    return response.data;
  }, {
    ...opts,
    retry: 0,
  });
};
